import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuthStore, useIsCaptain, useIsAdmin } from '../../store/authStore';

const DISMISSED_KEY = 'captain_onboarding_dismissed';

interface OnboardingContextValue {
  isOnboardingVisible: boolean;
  showOnboarding: () => void;
  hideOnboarding: (dismissPermanently: boolean) => void;
  isDismissedPermanently: boolean;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

interface OnboardingProviderProps {
  children: React.ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  const isCaptain = useIsCaptain();
  const isAdmin = useIsAdmin();
  const [isOnboardingVisible, setIsOnboardingVisible] = useState(false);

  const isDismissedPermanently = localStorage.getItem(DISMISSED_KEY) === 'true';
  const hasCompletedGettingStarted = user?.tours_completed?.includes('getting_started') ?? false;

  // Auto-show after 2s for captains (not admins) who haven't dismissed and haven't completed getting_started
  useEffect(() => {
    if (!isAuthenticated || !isCaptain || isAdmin || isDismissedPermanently || hasCompletedGettingStarted) {
      setIsOnboardingVisible(false);
      return;
    }

    const timer = setTimeout(() => setIsOnboardingVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [isAuthenticated, isCaptain, isAdmin, isDismissedPermanently, hasCompletedGettingStarted]);

  const showOnboarding = () => setIsOnboardingVisible(true);

  const hideOnboarding = (dismissPermanently: boolean) => {
    setIsOnboardingVisible(false);
    if (dismissPermanently) {
      localStorage.setItem(DISMISSED_KEY, 'true');
    }
  };

  const contextValue: OnboardingContextValue = {
    isOnboardingVisible,
    showOnboarding,
    hideOnboarding,
    isDismissedPermanently,
  };

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
};
